// src/components/mitchRoom/DraftRankings.js
import React, { useEffect, useState, useCallback, memo } from 'react';
import PropTypes from 'prop-types';
import { ChevronDown, Loader2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { getDfcDraftRankings, submitDraftRankings } from '../../services/api';
import { POSITIONS } from '../../utils/constants';
import UsernameEditor from './UsernameEditor';
import LoadingState from 'components/shared/LoadingState';
import { Alert, AlertDescription } from 'components/ui/alert';
import { Badge } from 'components/ui/badge';
import { Button } from 'components/ui/button';
import { Checkbox } from 'components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from 'components/ui/popover';
import { ToggleGroup, ToggleGroupItem } from 'components/ui/toggle-group';
import { cn } from 'lib/utils';

// ─────────────────────── dnd-kit ───────────────────────
import { DndContext, closestCenter } from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
  useSortable,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
// ────────────────────────────────────────────────────────

const SOURCES = ['OFPL Draft'];
const images = require.context('../../assets', false, /\.png$/);

// Utility sort for unranked list
/* fpl_draft_rank is draft-bootstrap-only — null for every player while data
   is served from the classic API (preseason). Missing ranks sink to the
   bottom and fall back to name order so the pool stays browsable. */
const sortByFplRank = (a, b) =>
  (a.fpl_draft_rank ?? Infinity) - (b.fpl_draft_rank ?? Infinity) ||
  (a.web_name || '').localeCompare(b.web_name || '');

const RankedCard = memo(({ el, team, moveToUnranked }) => (
  <div className="flex items-center justify-between gap-2 rounded-token-medium border border-borderGrey bg-white px-2 py-1">
    <div className="flex min-w-0 items-center gap-2">
      <span className="shrink-0 text-token-base text-neutral-500">{el.dfc_rank} |</span>
      {team && (
        <img
          src={images(`./Team=${team.name}.png`)}
          alt={team.short_name}
          className="h-6 w-6 shrink-0 object-contain"
        />
      )}
      <span className="truncate text-token-md">{el.web_name}</span>
      <Badge variant="outline" className="shrink-0">
        {el.singular_name_short}
      </Badge>
      <span className="shrink-0 text-token-sm text-neutral-500">
        {`Initial Rank: ${el.fpl_draft_rank || '-'}`}
      </span>
    </div>

    {/* stopPropagation keeps click from turning into a drag */}
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 shrink-0"
      onClick={() => moveToUnranked(el)}
      onPointerDown={(e) => e.stopPropagation()}
      aria-label={`Move ${el.web_name} to unranked`}
    >
      <Trash2 />
    </Button>
  </div>
));

const UnrankedCard = memo(({ el, team, moveToRanked }) => (
  <div className="flex items-center justify-between gap-2 rounded-token-medium border border-borderGrey bg-white px-2 py-1">
    <div className="flex min-w-0 items-center gap-2">
      {team && (
        <img
          src={images(`./Team=${team.name}.png`)}
          alt={team.short_name}
          className="h-6 w-6 shrink-0 object-contain"
        />
      )}
      <span className="truncate text-token-md">{el.web_name}</span>
      <Badge variant="outline" className="shrink-0">
        {el.singular_name_short}
      </Badge>
      <span className="shrink-0 text-token-sm text-neutral-500">
        {`Initial Rank: ${el.fpl_draft_rank || '-'}`}
      </span>
    </div>

    {/* pointerDown stop for symmetry, though not strictly required here */}
    <Button
      variant="ghost"
      size="icon"
      className="h-7 w-7 shrink-0"
      onClick={() => moveToRanked(el)}
      onPointerDown={(e) => e.stopPropagation()}
      aria-label={`Move ${el.web_name} to ranked`}
    >
      <Plus />
    </Button>
  </div>
));

/* ───── Sortable wrapper for ranked items ───── */
const SortableRankedItem = memo(({ el, team, moveToUnranked, disabled }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: el.element,
    disabled,
  });

  const style = {
    transform: CSS.Transform.toString(
      transform ? { ...transform, x: 0 } : transform // extra guard: no horizontal offset
    ),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(!disabled && 'cursor-grab active:cursor-grabbing')}
      {...attributes}
      {...listeners}
    >
      <RankedCard el={el} team={team} moveToUnranked={moveToUnranked} />
    </div>
  );
});

export default function DraftRankings({ user }) {
  const [data, setData] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingSubmit, setLoadingSubmit] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [source, setSource] = useState('OFPL Draft');
  const [ranked, setRanked] = useState([]);
  const [unranked, setUnranked] = useState([]);
  const [filterTeams, setFilterTeams] = useState([]);
  const [filterPositions, setFilterPositions] = useState([]);

  // Dirty-state tracking: the ranked order (which fully determines dfc_rank)
  // at last load / last successful submit.
  const [baseline, setBaseline] = useState(null);
  const signature = ranked.map((p) => p.element).join(',');
  const isDirty = baseline !== null && signature !== baseline;

  /* ─────────── Fetch draft data ─────────── */
  const fetchData = useCallback(() => {
    const wpId = user?.wp_id ?? user?.user?.wp_id;
    if (!wpId) {
      setLoadError('Missing wp_id');
      setLoadingData(false);
      return;
    }
    setLoadError(null);
    setLoadingData(true);
    getDfcDraftRankings(wpId, source)
      .then((res) => {
        setData(res);

        // annotate ranks
        const rankMap = new Map((res.dfc_rankings || []).map((r) => [r.element, r.dfc_rank]));
        const annotated = res.elements.map((el) => {
          const rank = rankMap.get(el.element);
          return {
            ...el,
            // Convert 0 to null, otherwise use the rank if it exists
            dfc_rank: rank === 0 || rank === undefined ? null : rank,
          };
        });

        const r = annotated
          .filter((el) => el.dfc_rank !== null)
          .sort((a, b) => a.dfc_rank - b.dfc_rank);

        const u = annotated.filter((el) => el.dfc_rank === null).sort(sortByFplRank);

        setRanked(r);
        setUnranked(u);
        setBaseline(r.map((p) => p.element).join(','));
      })
      .catch((err) => setLoadError(err.message))
      .finally(() => setLoadingData(false));
  }, [user, source]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ─────────── Username update handler ─────────── */
  const handleUsernameUpdate = useCallback((newUsername) => {
    setData((prev) => ({ ...prev, nice_username: newUsername }));
  }, []);

  /* ─────────── Filter helpers ─────────── */
  const teamOptions = data?.teams.map((t) => ({ label: t.short_name, value: t.team_fplID })) || [];
  const teamMap = new Map(data?.teams.map((t) => [t.team_fplID, t]));
  const filtersActive = filterTeams.length > 0 || filterPositions.length > 0;

  const toggleTeam = (teamId) => {
    setFilterTeams((prev) =>
      prev.includes(teamId) ? prev.filter((id) => id !== teamId) : [...prev, teamId]
    );
  };

  // summary counts
  const summaryCounts = ranked.reduce(
    (acc, p) => {
      acc[p.singular_name_short]++;
      return acc;
    },
    { GKP: 0, DEF: 0, MID: 0, FWD: 0 }
  );

  // filtered lists
  const filteredRanked = ranked.filter(
    (p) =>
      (!filterTeams.length || filterTeams.includes(p.team_fplID)) &&
      (!filterPositions.length || filterPositions.includes(p.singular_name_short))
  );
  const filteredUnranked = unranked.filter(
    (p) =>
      (!filterTeams.length || filterTeams.includes(p.team_fplID)) &&
      (!filterPositions.length || filterPositions.includes(p.singular_name_short))
  );

  /* ─────────── Rank/unrank mutations ─────────── */
  const getNextRank = useCallback(
    () => (ranked.length ? Math.max(...ranked.map((p) => p.dfc_rank)) + 1 : 1),
    [ranked]
  );

  const moveToRanked = useCallback(
    (player) => {
      setUnranked((u) => u.filter((p) => p.element !== player.element));
      setRanked((r) => [...r, { ...player, dfc_rank: getNextRank() }]);
    },
    [getNextRank]
  );

  const moveToUnranked = useCallback((player) => {
    // 1) remove from ranked and reassign ranks so they stay contiguous
    setRanked((r) => {
      const next = r.filter((p) => p.element !== player.element);
      return next.map((p, i) => ({ ...p, dfc_rank: i + 1 }));
    });

    // 2) add to unranked with dfc_rank explicitly nulled out
    setUnranked((u) => [...u, { ...player, dfc_rank: null }].sort(sortByFplRank));
  }, []);

  /* ─────────── dnd-kit onDragEnd ─────────── */
  const onDragEnd = useCallback(({ active, over }) => {
    if (!over || active.id === over.id) return;
    setRanked((r) => {
      const oldIndex = r.findIndex((p) => p.element === active.id);
      const newIndex = r.findIndex((p) => p.element === over.id);
      return arrayMove(r, oldIndex, newIndex).map((p, i) => ({ ...p, dfc_rank: i + 1 }));
    });
  }, []);

  /* ─────────── Submit to API ─────────── */
  const handleSubmitRankings = useCallback(() => {
    if (loadingSubmit) return;
    setLoadingSubmit(true);
    const combined = [...ranked, ...unranked].map((p) => ({
      element: p.element,
      dfc_rank: p.dfc_rank ?? null,
    }));
    submitDraftRankings(user.user.wp_id, source, combined)
      .then(() => {
        setBaseline(ranked.map((p) => p.element).join(','));
        toast.success('Rankings submitted');
      })
      .catch((err) => toast.error(err?.message || 'Failed to submit rankings'))
      .finally(() => setLoadingSubmit(false));
  }, [loadingSubmit, ranked, unranked, user, source]);

  /* ─────────── Cmd+S / Ctrl+S to submit ─────────── */
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (isDirty && !loadingSubmit) handleSubmitRankings();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isDirty, loadingSubmit, handleSubmitRankings]);

  if (loadError)
    return (
      <Alert variant="destructive" className="max-w-xl">
        <AlertDescription className="flex items-center justify-between gap-4">
          <span>{loadError}</span>
          <Button variant="outline" size="sm" onClick={fetchData}>
            Retry
          </Button>
        </AlertDescription>
      </Alert>
    );

  /* ─────────────────── UI ─────────────────── */
  return (
    <div className="flex flex-col gap-4">
      {/* Source toggle - only show if multiple sources */}
      {SOURCES.length > 1 && (
        <ToggleGroup
          type="single"
          value={source}
          onValueChange={(val) => val && setSource(val)}
          className="self-start"
        >
          {SOURCES.map((label) => (
            <ToggleGroupItem key={label} value={label} size="sm">
              {label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      )}

      {/* Username Editor Component */}
      {data && (
        <UsernameEditor
          user={user}
          initialUsername={data.nice_username}
          onUsernameUpdate={handleUsernameUpdate}
        />
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="outline" size="sm" className="gap-1">
              Teams{filterTeams.length > 0 ? `: ${filterTeams.length}` : ''}
              <ChevronDown />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="max-h-[300px] w-60 overflow-y-auto p-1">
            {teamOptions.map((option) => {
              const team = teamMap.get(option.value);
              return (
                <label
                  key={option.value}
                  className="flex cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-token-md hover:bg-neutral-50"
                >
                  <Checkbox
                    checked={filterTeams.includes(option.value)}
                    onCheckedChange={() => toggleTeam(option.value)}
                  />
                  {team && (
                    <img
                      src={images(`./Team=${team.name}.png`)}
                      alt=""
                      className="h-4 w-4 object-contain"
                    />
                  )}
                  {option.label}
                </label>
              );
            })}
            {teamOptions.length === 0 && (
              <p className="p-2 text-token-md text-muted-foreground">No teams loaded yet.</p>
            )}
          </PopoverContent>
        </Popover>

        <ToggleGroup type="multiple" value={filterPositions} onValueChange={setFilterPositions}>
          {POSITIONS.map((pos) => (
            <ToggleGroupItem key={pos} value={pos} size="sm">
              {pos}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>

        {filtersActive && (
          <Button variant="ghost" size="sm" onClick={() => { setFilterTeams([]); setFilterPositions([]); }}>
            Clear filters
          </Button>
        )}

        {filtersActive && (
          <span className="text-token-base text-muted-foreground">
            Drag reordering is disabled while filters are active
          </span>
        )}
      </div>

      {/* Lists & summary */}
      {loadingData ? (
        <LoadingState message="Loading draft rankings..." />
      ) : (
        <>
          {/* Summary counts */}
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-token-md font-medium">Summary:</span>
            {Object.entries(summaryCounts).map(([pos, count]) => (
              <Badge key={pos} variant="outline">{`${pos}: ${count}`}</Badge>
            ))}
          </div>

          <div className="flex flex-wrap items-start gap-4">
            {/* Ranked (sortable) */}
            <div className="flex max-h-[700px] w-full flex-col overflow-hidden rounded-token-large border border-borderGrey bg-white md:w-[420px]">
              <div className="flex items-center justify-between border-b border-borderGrey px-3 py-2">
                <h3 className="text-token-md font-semibold">Ranked</h3>
                <span className="text-token-base text-muted-foreground">{ranked.length}</span>
              </div>
              <div className="flex flex-col gap-1 overflow-y-auto overflow-x-hidden p-2">
                <DndContext
                  modifiers={[restrictToVerticalAxis]} // <── prevents horizontal drag
                  onDragEnd={onDragEnd}
                  collisionDetection={closestCenter}
                >
                  <SortableContext
                    items={filteredRanked.map((el) => el.element)}
                    strategy={verticalListSortingStrategy}
                  >
                    {filteredRanked.map((el) => (
                      <SortableRankedItem
                        key={el.element}
                        el={el}
                        team={teamMap.get(el.team_fplID)}
                        moveToUnranked={moveToUnranked}
                        disabled={filtersActive}
                      />
                    ))}
                  </SortableContext>
                </DndContext>
                {filteredRanked.length === 0 && (
                  <p className="p-4 text-center text-token-md text-muted-foreground">
                    {filtersActive ? 'No ranked players match the filters.' : 'No ranked players yet.'}
                  </p>
                )}
              </div>
            </div>

            {/* Unranked */}
            <div className="flex max-h-[700px] w-full flex-col overflow-hidden rounded-token-large border border-borderGrey bg-white md:w-[420px]">
              <div className="flex items-center justify-between border-b border-borderGrey px-3 py-2">
                <h3 className="text-token-md font-semibold">Unranked</h3>
                <span className="text-token-base text-muted-foreground">{unranked.length}</span>
              </div>
              <div className="flex flex-col gap-1 overflow-y-auto p-2">
                {filteredUnranked.map((el) => (
                  <UnrankedCard
                    key={el.element}
                    el={el}
                    team={teamMap.get(el.team_fplID)}
                    moveToRanked={moveToRanked}
                  />
                ))}
                {filteredUnranked.length === 0 && (
                  <p className="p-4 text-center text-token-md text-muted-foreground">
                    {filtersActive ? 'No unranked players match the filters.' : 'No unranked players.'}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Submit */}
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={handleSubmitRankings} disabled={!isDirty || loadingSubmit}>
              {loadingSubmit && <Loader2 className="animate-spin" />}
              Submit Rankings
            </Button>
            {isDirty ? (
              <>
                <Badge variant="warning">Unsaved changes</Badge>
                <span className="text-token-base text-muted-foreground">Ctrl/Cmd+S to submit</span>
              </>
            ) : (
              <span className="text-token-base text-muted-foreground">All changes saved</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}

DraftRankings.propTypes = {
  user: PropTypes.object.isRequired,
};
